/**
 * Backoffice users (GET/POST/PATCH/DELETE /api/backoffice-users) and the operator list for bindings.
 * Traps (rgs-api-contract): sorting/filtering on `Username`/`Status` gives a raw 500 (not on the
 * entity), so only Id/Email/CreatedAt are offered; bodies aren't validated server-side, so only the
 * edited fields are sent; `UPDATE_USERS` alone can grant any bit server-side (the editor guards it
 * in the UI only); delete fails once the user has audit-log rows.
 */
import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { http } from '@/api/http';
import {
  encodePagedQuery,
  pagedSchema,
  type FilterDescriptor,
  type PagedQuery,
  type SortDescriptor,
} from '@/api/paged-query';
import { bigintString } from '@/api/zod-helpers';
import { listSearchSchema, toPagedQuery } from '@/lib/url-state';

// The response never includes `Password` (the server strips the hash); z.object drops it anyway.
export const backofficeUserSchema = z.object({
  Id: z.coerce.number().int(),
  OperatorId: z.coerce
    .number()
    .int()
    .nullish()
    .transform((v) => v ?? null),
  Email: z.string(),
  FullName: z
    .string()
    .nullish()
    .transform((v) => v ?? ''),
  Permission: bigintString,
  /** A login claim only; never used for authorization. */
  Brand: z
    .string()
    .nullish()
    .transform((v) => v ?? null),
  CreatedAt: z
    .string()
    .nullish()
    .transform((v) => v ?? null),
  UpdatedAt: z
    .string()
    .nullish()
    .transform((v) => v ?? null),
});

export type BackofficeUser = z.infer<typeof backofficeUserSchema>;

export const USER_SORT_FIELDS = ['Id', 'Email', 'CreatedAt'] as const;
export const DEFAULT_USER_SORT: SortDescriptor = { field: 'Id', dir: 'desc' };

// `filters: {}` keeps the inferred type exact (without it the generic widens to Zod's raw shape).
export const usersSearchSchema = listSearchSchema({ sortFields: USER_SORT_FIELDS, filters: {} });
export type UsersSearch = z.infer<typeof usersSearchSchema>;

export function usersPagedQuery(search: UsersSearch): PagedQuery {
  const filters: FilterDescriptor[] = [];
  if (search.q) filters.push({ field: 'Email', operator: 'contains', value: search.q });
  return toPagedQuery(search, { defaultSort: DEFAULT_USER_SORT, filters });
}

export const usersKeys = {
  all: ['backoffice-users'] as const,
  list: (query: PagedQuery) => ['backoffice-users', 'list', query] as const,
  detail: (id: number) => ['backoffice-users', 'detail', id] as const,
  directory: ['backoffice-users', 'directory'] as const,
};

const listSchema = pagedSchema(backofficeUserSchema);

export const usersListQuery = (search: UsersSearch) => {
  const query = usersPagedQuery(search);
  return queryOptions({
    queryKey: usersKeys.list(query),
    queryFn: ({ signal }) => http('/backoffice-users', { query: encodePagedQuery(query), signal }, listSchema),
    placeholderData: keepPreviousData,
  });
};

export const userQuery = (id: number) =>
  queryOptions({
    queryKey: usersKeys.detail(id),
    queryFn: ({ signal }) => http(`/backoffice-users/${id}`, { signal }, backofficeUserSchema),
  });

/** Id → name for the audit log. Backoffice users are few; one page covers them. */
export const userDirectoryQuery = () =>
  queryOptions({
    queryKey: usersKeys.directory,
    queryFn: async ({ signal }) => {
      const page = await http(
        '/backoffice-users',
        { query: encodePagedQuery({ skip: 0, take: 500, sort: { field: 'Id', dir: 'asc' } }), signal },
        listSchema,
      );
      return new Map(page.data.map((u) => [u.Id, u.FullName || u.Email] as const));
    },
    staleTime: 5 * 60_000,
  });

// ---- operators, for the binding ----------------------------------------------------------------

// Only Id and Name: the operators list also carries each operator's ApiSalt, which never enters here.
const operatorOptionSchema = z.object({ Id: z.coerce.number().int(), Name: z.string() });

export const operatorOptionsQuery = () =>
  queryOptions({
    queryKey: ['backoffice-users', 'operator-options'],
    queryFn: async ({ signal }) => {
      const page = await http(
        '/operators',
        { query: encodePagedQuery({ skip: 0, take: 500, sort: { field: 'Name', dir: 'asc' } }), signal },
        pagedSchema(operatorOptionSchema),
      );
      return page.data;
    },
    staleTime: 5 * 60_000,
  });

// ---- writes ------------------------------------------------------------------------------------

export interface UserWrite {
  FullName?: string;
  Email?: string;
  /** Plain text over TLS; the server hashes it. Never kept after the request. */
  Password?: string;
  /** Required on create; every account belongs to an operator, super admins included. */
  OperatorId?: number;
  Brand?: string | null;
  /** BIGINT bitmask as a decimal string. */
  Permission?: string;
}

export function useCreateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UserWrite) => http('/backoffice-users', { method: 'POST', body }, backofficeUserSchema),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: usersKeys.all });
    },
  });
}

export function useUpdateUser(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UserWrite) => http(`/backoffice-users/${id}`, { method: 'PATCH', body }, backofficeUserSchema),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: usersKeys.all });
    },
  });
}

export function useDeleteUser(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => http(`/backoffice-users/${id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      queryClient.removeQueries({ queryKey: usersKeys.detail(id) });
      await queryClient.invalidateQueries({ queryKey: usersKeys.all });
    },
  });
}
